import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { compareByMenuOrder, menuGroupOf } from '@/config/navigation';
import { useMenuLabels } from '@/features/menuLabels/hooks/useMenuLabels';
import { showApiErrorToast } from '@/utils/errors';
import {
  permissionQueryKeys,
  setUserRoles,
  userPermissionsQueryOptions,
  userRolesQueryOptions,
} from '../api/permissions.api';
import { rolesQueryOptions } from '../api/roles.api';
import { groupByModule } from '../types/permission.types';
import type { EffectivePermission } from '../types/permission.types';

const NO_ROLES: ReadonlySet<number> = new Set();
const DIRECT_GRANT_LABEL = 'a direct grant';

export function useUserRoles(userId: number) {
  const queryClient = useQueryClient();
  const { labelOf } = useMenuLabels();
  const [editedIds, setEditedIds] = useState<ReadonlySet<number> | null>(null);

  const roles = useQuery(rolesQueryOptions());
  const held = useQuery(userRolesQueryOptions(userId));
  const effective = useQuery(userPermissionsQueryOptions(userId));

  const heldIds = useMemo(() => new Set((held.data ?? []).map((role) => role.id)), [held.data]);
  const selectedIds = editedIds ?? (held.data ? heldIds : NO_ROLES);

  const viaRoleByCode = useMemo(() => {
    const byCode = new Map<string, string[]>();
    for (const permission of effective.data ?? []) {
      const via = permission.viaRoleCode ?? DIRECT_GRANT_LABEL;
      const existing = byCode.get(permission.code);
      if (existing) existing.push(via);
      else byCode.set(permission.code, [via]);
    }
    return byCode;
  }, [effective.data]);

  const effectiveGroups = useMemo(() => {
    const unique = new Map<string, EffectivePermission>();
    for (const permission of effective.data ?? []) {
      if (!unique.has(permission.code)) unique.set(permission.code, permission);
    }
    return groupByModule(
      [...unique.values()]
        .map((permission) => ({
          ...permission,
          module: labelOf(menuGroupOf(permission.code)),
        }))
        .sort((left, right) => compareByMenuOrder(left.code, right.code)),
    );
  }, [effective.data, labelOf]);

  const addedCount = [...selectedIds].filter((id) => !heldIds.has(id)).length;
  const removedCount = [...heldIds].filter((id) => !selectedIds.has(id)).length;

  const mutation = useMutation({
    mutationFn: () => setUserRoles(userId, [...selectedIds]),
    onSuccess: async () => {
      toast.success('Roles saved');
      await queryClient.invalidateQueries({ queryKey: permissionQueryKeys.all() });
      setEditedIds(null);
    },
    onError: (error) => showApiErrorToast(error, 'Could not save the roles'),
  });

  return {
    roles: roles.data ?? [],
    isLoading: roles.isLoading || held.isLoading,
    error: roles.error ?? held.error,
    selectedIds,
    toggle: (id: number) =>
      setEditedIds((current) => {
        const next = new Set(current ?? selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    isDirty: addedCount > 0 || removedCount > 0,
    reset: () => setEditedIds(null),
    save: () => mutation.mutate(),
    isSaving: mutation.isPending,
    effectiveGroups,
    viaRoleByCode,
    isEffectiveLoading: effective.isLoading,
    effectiveError: effective.error,
  };
}
